import { useAppSelector } from "../../../../hooks";
import { NotExist } from "../../../layouts/NotData";
import { Card, cn, Flex, Loading, Typography } from "djuno-design";
import { useNavigate } from "react-router-dom";
import {
  selectAiJob,
  selectAiJobLoading,
} from "../../../../store/ai/jobs/jobSlice";
import { AiJobStatusTag } from "../../../../pages/ai-trainings/AiJobsPage";
import { formatDuration, formatTimestamp } from "../../../../utils/date";
import CheckIcon from "./../../../../assets/icons/check.svg?react";
import { humanizeSize } from "../../../../utils/file";
import { AiTrainingsUrl } from "../../../../utils/urls";
import { AiJobStatusState } from "../../../../types/ai";

const AiJobInformationTab = () => {
  const job = useAppSelector(selectAiJob);
  const jobLoading = useAppSelector(selectAiJobLoading);

  const navigate = useNavigate();
  // const dispatch = useAppDispatch();

  return (
    <>
      {job && !jobLoading && (
        <Flex direction="col" className="gap-5">
          <Card title="Job information">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Name
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {job.spec.name}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Region
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {job.spec.region}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Creation
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {
                      formatTimestamp(job.createdAt, "D MMM YYYY HH:mm:ss", {
                        isUTC: false,
                      }).datetime
                    }
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Timeline
                  </Typography.Title>
                  {job.status.history.map((history, index) => (
                    <Typography.Text
                      key={index}
                      className="!text-xs flex items-center gap-1 mb-1"
                      uiType={
                        job.status.history.length === index + 1
                          ? undefined
                          : "secondary"
                      }
                    >
                      <CheckIcon
                        className={cn("w-4 flex-shrink-0 ", {
                          "text-primary-500 scale-110":
                            job.status.history.length === index + 1,
                        })}
                      />
                      <AiJobStatusTag
                        state={history.state as AiJobStatusState}
                      />
                      :{"  "}
                      {
                        formatTimestamp(history.date, "D MMM YYYY HH:mm:ss", {
                          isUTC: false,
                        }).datetime
                      }
                    </Typography.Text>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card title="Container">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Docker Image
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {job.spec.image}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Status
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    <AiJobStatusTag state={job.status.state} />
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Execution time
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {formatDuration(job.status.duration)}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Resources">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  {job.spec.resources.cpu > 0 && (
                    <Typography.Text className="!text-sm flex items-center gap-1">
                      CPU: {job.spec.resources.cpu}
                    </Typography.Text>
                  )}

                  {job.spec.resources.gpu > 0 && (
                    <Typography.Text className="!text-sm flex items-center gap-1">
                      GPU: {job.spec.resources.gpu}
                    </Typography.Text>
                  )}

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    RAM: {humanizeSize(job.spec.resources.memory).join("B")}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </Card>
        </Flex>
      )}

      {/* empty service */}
      {job === null && !jobLoading && (
        <div className="h-full flex items-center justify-center px-4">
          <div className="flex flex-col items-center">
            <div className="text-center space-y-1">
              <NotExist
                url={() => navigate(AiTrainingsUrl)}
                text="The job does not exist"
              />
            </div>
          </div>
        </div>
      )}

      {jobLoading && (
        <Flex items="center" justify="center" className="w-full !min-h-[300px]">
          <Loading borderSize={2} />
        </Flex>
      )}
    </>
  );
};

export default AiJobInformationTab;
